import React from "react";

const getScrollDelta = (event: React.WheelEvent<HTMLDivElement>) =>
  Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;

export const useHorizontalDragScroll = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef({
    pointerId: -1,
    startX: 0,
    scrollLeft: 0,
  });
  const [isDragging, setIsDragging] = React.useState(false);

  const isScrollable = React.useCallback(() => {
    const el = scrollRef.current;
    return Boolean(el && el.scrollWidth > el.clientWidth);
  }, []);

  const stopDragging = React.useCallback(() => {
    dragRef.current.pointerId = -1;
    setIsDragging(false);
  }, []);

  const handleWheel = React.useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      if (!el || !isScrollable()) return;

      const delta = getScrollDelta(event);
      if (delta === 0) return;

      event.preventDefault();
      el.scrollLeft += delta;
    },
    [isScrollable],
  );

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      if (!el || !isScrollable()) return;

      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        scrollLeft: el.scrollLeft,
      };
      el.setPointerCapture(event.pointerId);
      setIsDragging(true);
    },
    [isScrollable],
  );

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      const dragState = dragRef.current;
      if (!el || dragState.pointerId !== event.pointerId) return;

      event.preventDefault();
      el.scrollLeft = dragState.scrollLeft - (event.clientX - dragState.startX);
    },
    [],
  );

  const handlePointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      if (el?.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      stopDragging();
    },
    [stopDragging],
  );

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleWheel,
    isDragging,
    scrollRef,
  };
};
